<template>
  <div data-perfect-scrollbar>
    <b-skeleton-wrapper :loading="isLoading" v-if="isLoading">
      <template #loading>
        <div class="page-section pb10 student-page">
          <div class="container page__container d-flex align-items-center">
            <p class="backResult text-50">
              <router-link :to="{ name: 'student-org-research-listing' }" style="text-decoration: underline">
                <span class="material-icons icon-18pt text-80">navigate_before</span>Back to Research
              </router-link>
            </p>
          </div>

          <div
            class="container page__container d-flex flex-column flex-md-row align-items-center text-center text-md-left"
          >
            <b-col md="2">
              <b-skeleton-img height="113px" no-aspect></b-skeleton-img>
            </b-col>
            <div class="flex mb-32pt mb-md-0 mobilemb0">
              <h4 class="mb-0"><b-skeleton width="60%" height="1.5rem"></b-skeleton></h4>
              <p class="d-flex mt-2 mb-3">
                <b-skeleton width="85%"></b-skeleton>
              </p>

              <b-row>
                <b-col md="2" sm="12">
                  <b-skeleton type="button" width="100%"></b-skeleton>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>

        <div class="container page__container page-section student-card mt-0 pt-0 mb-32pt">
          <div class="card">
            <div class="card-body">
              <div class="d-flex mb-1">
                <div class="flex">
                  <div class="mb-1">
                    <h5 class="card-title fw600"><b-skeleton width="50%"></b-skeleton></h5>
                  </div>
                  <div class="d-flex">
                    <div class="flex">
                      <span class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">location_on</i><b-skeleton width="75%"></b-skeleton>
                      </span>
                      <span class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">timelapse</i><b-skeleton width="50%"></b-skeleton>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card">
            <div class="card-body">
              <div class="d-flex mb-1">
                <div class="flex">
                  <div class="mb-1">
                    <h5 class="card-title fw600"><b-skeleton width="50%"></b-skeleton></h5>
                  </div>
                  <span>
                    <b-skeleton width="70%"></b-skeleton>
                    <b-skeleton width="85%"></b-skeleton>
                    <b-skeleton width="60%"></b-skeleton>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-skeleton-wrapper>

    <div v-else-if="research">
      <div class="page-section pb10 student-page">
        <div class="container page__container d-flex align-items-center">
          <p class="backResult text-50">
            <router-link :to="{ name: 'student-org-research-listing' }" style="text-decoration: underline">
              <span class="material-icons icon-18pt text-80">navigate_before</span>Back to Research
            </router-link>
          </p>
        </div>

        <div
          class="container page__container d-flex flex-column flex-md-row align-items-center text-center text-md-left"
        >
          <img
            :src="researchLogo"
            width="115"
            class="mr-md-32pt mb-32pt mb-md-0 rounded mobilemb10"
            alt="Research Logo"
          />
          <div class="flex mb-32pt mb-md-0 mobilemb0">
            <h4 class="mb-0">
              {{ research.title }}
              <b-badge pill variant="primary" v-if="research.research_type !== JOB_TYPES.EXTERNAL">Featured</b-badge>
            </h4>
            <p class="d-flex align-items-center quote mt-1 mb-4" v-if="postedByName">
              <i class="material-icons icon-12pt mr-8pt text-50">account_balance</i>
              <a href="#" @click.prevent class="text-primary mr-2" style="display: inline; font-weight: 600">{{
                postedByName
              }}</a>
            </p>

            <a
              :href="getClickableLink(research.application_link)"
              class="btn btn-primary"
              v-if="research.application_link"
              target="_blank"
              ><md-icon class="mr-1">open_in_new</md-icon>Apply Now</a
            >
            <b-dropdown
              text="Contact"
              variant="primary"
              class="ml-2"
              v-if="
                (research.email_contact_allowed && research.contact_email) ||
                (research.phone_contact_allowed && research.contact_phone)
              "
            >
              <b-dropdown-item
                :href="`mailto:${research.contact_email}`"
                v-if="research.email_contact_allowed && research.contact_email"
                ><i class="material-icons mr-1">email</i>Email</b-dropdown-item
              >
              <b-dropdown-item
                :href="`tel:${research.contact_phone}`"
                v-if="research.phone_contact_allowed && research.contact_phone"
                ><i class="material-icons mr-1">phone</i>Call</b-dropdown-item
              >
            </b-dropdown>
          </div>
        </div>
      </div>

      <div class="container page__container page-section mt-0 pt-0 mb-32pt">
        <div class="student-card" v-if="hasBasicInfo">
          <div class="card">
            <div class="card-body">
              <div class="d-flex mb-1">
                <div class="flex">
                  <div class="d-flex align-items-center mb-1">
                    <h5 class="card-title fw600">Basic Info</h5>
                  </div>

                  <div class="d-flex">
                    <div class="flex">
                      <span v-if="research.faculty_name" class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">person</i>Faculty / PI: {{ research.faculty_name }}
                      </span>
                      <span v-if="research.department" class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">apartment</i>Department: {{ research.department }}
                      </span>
                      <span v-if="research.location" class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">place</i>{{ research.location }}
                      </span>
                      <span v-if="fullAddress" class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">location_on</i>{{ fullAddress }}
                      </span>
                      <span v-if="research.is_remote" class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">wifi</i>Remote opportunity
                      </span>
                      <span class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">{{
                          research.is_paid ? 'payments' : 'volunteer_activism'
                        }}</i
                        >{{ research.is_paid ? 'Paid' : 'Unpaid' }}
                        opportunity<span v-if="research.is_paid && research.stipend">
                          — Stipend: ${{ $n(research.stipend) }}</span
                        >
                      </span>
                      <span v-if="research.time_commitment" class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">hourglass_empty</i>Time commitment:
                        {{ research.time_commitment }}
                      </span>
                      <span v-if="research.deadline" class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">event</i>Application deadline:
                        {{ formatFullDate(research.deadline) }}
                      </span>
                      <span v-if="research.type" class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">timelapse</i>Seeking candidates for
                        {{ $t(`jobWorkTypeOpts.${research.type}`) }} position.
                      </span>
                      <span v-if="research.schedule" class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">access_time</i>Schedule/Shift:
                        {{ $t(`jobScheduleOpts.${research.schedule}`) }}
                      </span>
                      <span v-if="research.no_of_hires" class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">group</i>No. of positions:
                        {{ research.no_of_hires }}
                      </span>
                      <span v-if="research.minimum_pay || research.maximum_pay" class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">attach_money</i>
                        <span v-if="research.maximum_pay"
                          >Pay Range: ${{ $n(research.minimum_pay) }} - ${{ $n(research.maximum_pay) }}</span
                        >
                        <span v-else>Pay starting from ${{ $n(research.minimum_pay) }}.</span>
                      </span>
                      <span v-if="research.pay_rate" class="icon-align">
                        <i class="material-icons icon-18pt mr-8pt">local_atm</i>Pay Rate:
                        {{ $t(`jobPayRateOpts.${research.pay_rate}`) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="event-card" v-if="research.description">
          <div class="card">
            <div class="card-body">
              <div class="d-flex mb-1">
                <div class="flex">
                  <div class="d-flex align-items-center mb-1">
                    <h5 class="card-title fw600">Research Description</h5>
                  </div>
                  <div v-safe-html="research.description" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="event-card" v-if="research.eligibility">
          <div class="card">
            <div class="card-body">
              <div class="d-flex mb-1">
                <div class="flex">
                  <div class="d-flex align-items-center mb-1">
                    <h5 class="card-title fw600">Eligibility</h5>
                  </div>
                  <span style="white-space: pre-line">{{ research.eligibility }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="student-card">
          <div class="row card-group-row" v-if="research.caregiving_experience">
            <div class="col-sm-12 col-xl-12">
              <div class="card card-sm">
                <div class="card-body">
                  <div>
                    <div>
                      <h5 class="card-title fw600">Care Experience</h5>
                    </div>
                    <span class="icon-align">
                      <i class="material-icons icon-18pt mr-8pt">favorite</i>{{ research.caregiving_experience }}
                      year(s) caregiving experience required.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card" v-if="certifications.length">
            <div class="card-body">
              <div class="d-flex mb-1">
                <div class="flex">
                  <div class="d-flex align-items-center mb-1">
                    <h5 class="card-title fw600">Required Certifications</h5>
                  </div>
                  <ul>
                    <li v-for="(cert, index) in certifications" :key="`${cert}-${index}`">
                      <span><i class="material-icons icon-18pt mr-8pt">assignment_turned_in</i>{{ cert }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="card" v-if="records.length">
            <div class="card-body">
              <div class="d-flex mb-1">
                <div class="flex">
                  <div class="d-flex align-items-center mb-1">
                    <h5 class="card-title fw600">Required Records</h5>
                  </div>
                  <ul>
                    <li v-for="(record, index) in records" :key="`${record}-${index}`">
                      <span><i class="material-icons icon-18pt mr-8pt">description</i>{{ record }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="card" v-if="benefitsTexts.length">
            <div class="card-body">
              <div class="d-flex mb-1">
                <div class="flex">
                  <div class="d-flex align-items-center mb-1">
                    <h5 class="card-title fw600">Benefits &amp; Perks</h5>
                  </div>
                  <ul>
                    <li v-for="(benefit, index) in benefitsTexts" :key="`${benefit}-${index}`">
                      <span><i class="material-icons icon-18pt mr-8pt">check</i>{{ benefit }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="card" v-if="careerAdvancements.length || research.career_offerings_details">
            <div class="card-body">
              <div class="d-flex mb-1">
                <div class="flex">
                  <div class="d-flex align-items-center mb-1">
                    <h5 class="card-title fw600">Career Advancements</h5>
                  </div>
                  <span v-if="research.career_offerings_details">{{ research.career_offerings_details }}</span>
                  <ul class="mt-2" v-if="careerAdvancements.length">
                    <li v-for="(adv, index) in careerAdvancements" :key="`${adv}-${index}`">
                      <span><i class="material-icons icon-18pt mr-8pt">local_hospital</i>{{ adv }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div class="card" v-if="associatedPrograms.length">
            <div class="card-body">
              <div class="d-flex mb-1">
                <div class="flex">
                  <div class="d-flex align-items-center mb-1">
                    <h5 class="card-title fw600">Associated Programs</h5>
                  </div>
                  <ul>
                    <li v-for="(program, index) in associatedPrograms" :key="`${program}-${index}`">
                      <span><i class="material-icons icon-18pt mr-8pt">school</i>{{ program }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="container page__container page-section">
      <div class="card card-block">
        <div class="card-body text-center">
          <h4>Research opportunity not found</h4>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Page from '@/components/Page.vue';
import { mapActions } from 'vuex';
import { formatTimeAgo, formatFullDate, getClickableLink } from '@/common/utils';
import { JOB_BENEFITS_OPTONS, JOB_CERT_OPTIONS, JOB_RECORD_OPTIONS, JOB_TYPES } from '../../common/constants';
import { get, keyBy } from 'lodash';
import DefaultAvatar from '@/assets/images/logos/logo_icon.png';

export default {
  name: 'ResearchShowPage',
  extends: Page,
  data() {
    return {
      title: 'Research Details',
      isLoading: true,
      research: null,
      JOB_TYPES,
      DefaultAvatar,
      formatTimeAgo,
      formatFullDate,
      getClickableLink,

      certifications: [],
      records: [],
      benefitsTexts: [],
      careerAdvancements: [],
      associatedPrograms: [],
    };
  },
  computed: {
    postedByName() {
      return get(this.research, 'universities[0].name') || get(this.research, 'ext_posted_by.name') || null;
    },
    researchLogo() {
      return (
        get(this.research, 'universities[0].small_logo_url') ||
        get(this.research, 'ext_posted_by.small_logo_url') ||
        this.DefaultAvatar
      );
    },
    fullAddress() {
      return [
        this.research.address,
        this.research.city,
        this.research.state,
        this.research.zipcode,
        this.research.country,
      ]
        .filter(Boolean)
        .join(', ');
    },
    hasBasicInfo() {
      // The paid/unpaid indicator always renders, so Basic Info is always shown
      // whenever a research record exists.
      return !!this.research;
    },
  },
  methods: {
    ...mapActions('research', ['getResearch']),
    get,
    buildOptionTexts(resp) {
      // Benefits
      (JOB_BENEFITS_OPTONS || []).forEach((benefit) => {
        if ((resp.benefits || []).includes(benefit.value) && benefit.value !== 'other') {
          this.benefitsTexts.push(benefit.text);
        }
      });
      if ((resp.benefits || []).includes('other') && resp.other_benefits) {
        this.benefitsTexts.push(resp.other_benefits);
      }

      // Career Advancements
      if (resp.offers_career_advancement) {
        this.careerAdvancements = (resp.career_offerings || []).map((career) => career.name);
        if (resp.other_career_offering) {
          this.careerAdvancements.push(resp.other_career_offering);
        }
      }

      // Required Certifications
      const certByValue = keyBy(JOB_CERT_OPTIONS, 'value');
      (resp.required_certs || []).forEach((cert) => {
        if (certByValue[cert] && cert !== 'other') {
          this.certifications.push(certByValue[cert].fullText);
        }
      });
      if ((resp.required_certs || []).includes('other') && resp.other_required_certs) {
        this.certifications.push(resp.other_required_certs);
      }

      // Required Records
      const recordsByValue = keyBy(JOB_RECORD_OPTIONS, 'value');
      (resp.required_records || []).forEach((record) => {
        if (recordsByValue[record] && record !== 'other') {
          this.records.push(recordsByValue[record].text);
        }
      });
      if ((resp.required_records || []).includes('other') && resp.other_required_records) {
        this.records.push(resp.other_required_records);
      }

      // Associated Programs
      this.associatedPrograms = (resp.associated_programs || []).map((program) => program.title).filter(Boolean);
    },
    async fetchResearch() {
      this.isLoading = true;
      try {
        const response = await this.getResearch(this.$route.params.id);
        this.buildOptionTexts(response.data);
        this.research = response.data;
      } catch (_err) {
        this.research = null;
      }
      this.isLoading = false;
    },
  },
  async mounted() {
    this.fetchResearch();
  },
};
</script>
